// Author: Ahsan Saleem
/**
 * Sign-in. The one BloomLabs wordmark moment (Cormorant); everything past this
 * screen is operational Inter. Falls back to pasting an access token for demos.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, getSites, login } from '../api.js';
import { session } from '../session.js';
import { Bloom } from '../ui/Bloom.js';

export function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  async function afterAuth() {
    const sites = await getSites();
    if (sites.length === 0) throw new Error('You have no site access');
    auth.siteId = sites[0].id;
    auth.siteName = sites[0].name;
    sessionStorage.removeItem('bloomos_booted'); // fresh sign-in gets the cold-start ritual
    navigate(session.lastPath(), { replace: true }); // restore where they left off
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(undefined);
    try {
      const res = await login(email, password);
      auth.token = res.accessToken;
      await afterAuth();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign in failed');
    } finally { setBusy(false); }
  }

  async function useToken() {
    setBusy(true); setError(undefined);
    try { auth.token = token.trim(); await afterAuth(); }
    catch (err) { setError(err instanceof Error ? err.message : 'Token rejected'); auth.token = null; }
    finally { setBusy(false); }
  }

  return (
    <div className="signin">
      <div className="signin-card">
        <div className="signin-brand">
          <Bloom size={40} state="idle" />
          <span className="signin-word">BloomOS</span>
        </div>
        <p className="signin-tag">Living-systems command centre</p>

        <form onSubmit={submit} className="signin-form">
          <label className="field">
            <span>Email</span>
            <input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@bloomlabs.mv" />
          </label>
          <label className="field">
            <span>Password</span>
            <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </label>
          <button className="btn btn-primary signin-submit" type="submit" disabled={busy}>{busy ? 'Signing in…' : 'Sign in'}</button>
        </form>

        {error && <p className="signin-error" role="alert">{error}</p>}

        <button className="signin-alt" onClick={() => setShowToken((s) => !s)}>Use an access token</button>
        {showToken && (
          <div className="signin-token">
            <textarea value={token} onChange={(e) => setToken(e.target.value)} placeholder="Paste a JWT access token" rows={3} />
            <button className="btn btn-sm" onClick={useToken} disabled={busy || !token.trim()}>Continue with token</button>
          </div>
        )}
      </div>
      <div className="signin-foot">BloomLabs · Controlled-environment operations</div>
    </div>
  );
}
